package at.theatervogelweide.domain

/**
 * An IBAN (#147), as the Impressum stores it: in capitals without spaces, checked by its check digits (ISO 13616), and
 * shown in groups of four.
 */
object Iban {
    const val MAX_LENGTH = 34
    private const val MIN_LENGTH = 15

    private val FORMAT = Regex("^[A-Z]{2}[0-9]{2}[A-Z0-9]+$")

    /** As typed, without spaces and in capitals: `at61 1904 3002` becomes `AT6119043002`. */
    fun normalize(input: String): String = input.filterNot { it.isWhitespace() }.uppercase()

    /** Whether [iban], [normalize]d, has the form of one and its check digits fit. */
    fun isValid(iban: String): Boolean {
        val normalized = normalize(iban)
        if (normalized.length !in MIN_LENGTH..MAX_LENGTH || !FORMAT.matches(normalized)) {
            return false
        }
        // the first four characters go to the end, each letter becomes two digits (A = 10), and the number mod 97 is 1
        val rearranged = normalized.drop(4) + normalized.take(4)
        var remainder = 0
        for (char in rearranged) {
            val value = if (char.isDigit()) char - '0' else char - 'A' + 10
            remainder = if (value >= 10) (remainder * 100 + value) % 97 else (remainder * 10 + value) % 97
        }
        return remainder == 1
    }

    /** In capitals, in groups of four separated by spaces, the last one shorter: `AT61 1904 3002 3457 3201`. */
    fun grouped(iban: String): String = normalize(iban).chunked(4).joinToString(" ")
}

/** A BIC (#147): 8 or 11 characters, stored in capitals without spaces. */
object Bic {
    const val MAX_LENGTH = 11

    // bank (4 letters), country (2 letters), location (2), and optionally a branch (3)
    private val FORMAT = Regex("^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$")

    fun normalize(input: String): String = input.filterNot { it.isWhitespace() }.uppercase()

    fun isValid(bic: String): Boolean = FORMAT.matches(normalize(bic))
}

/** A phone number as entered, such as `+43 699 12310780`. */
object PhoneNumber {
    // digits, a leading +, and spaces, slashes, hyphens or brackets between them
    private val FORMAT = Regex("""^\+?[0-9 ()/-]*[0-9][0-9 ()/-]*$""")

    /** Whether [phone] has at least one digit to dial and nothing but what numbers are written with. */
    fun isValid(phone: String): Boolean = FORMAT.matches(phone.trim())

    /** The `tel:` link for [phone]: only its digits and a leading `+`, as `tel:+4369912310780`. */
    fun telHref(phone: String): String {
        val trimmed = phone.trim()
        val digits = trimmed.filter { it.isDigit() }
        return "tel:" + (if (trimmed.startsWith("+")) "+" else "") + digits
    }
}
